import React, {
    useCallback, useEffect, useId, useLayoutEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import useDismiss from '../../hooks/useDismiss';
import { focusableIn } from './useModalFocus';
import './ui.css';

/**
 * A button that opens a small non-modal panel (Filters, Columns, Saved views).
 * Opening moves focus into the panel; Escape closes it and returns focus to
 * the trigger; a mousedown outside closes it without stealing focus from
 * whatever was clicked. Works uncontrolled, or controlled via open/onOpenChange.
 *
 * The panel always stays inside the viewport with a 16 px gutter: when the
 * trigger sits near an edge (a wrapped toolbar on a phone) it is shifted
 * back in, and it is never wider than the viewport minus both gutters.
 */
const GUTTER = 16;

export function fitPanelToViewport(panel, align) {
    if (!panel) return;
    panel.style.transform = '';
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const avail = Math.max(0, vw - 2 * GUTTER);
    panel.style.maxWidth = `${avail}px`;
    const rect = panel.getBoundingClientRect();
    if (!rect.width || !vw) return; // not laid out (tests, hidden)
    const w = Math.min(rect.width, avail);
    const left = align === 'right' ? rect.right - w : rect.left;
    let shift = 0;
    if (left < GUTTER) shift = GUTTER - left;
    else if (left + w > vw - GUTTER) shift = vw - GUTTER - (left + w);
    if (shift) panel.style.transform = `translateX(${Math.round(shift)}px)`;
}

export function Popover({
    triggerLabel, triggerContent, title, children, align = 'left', width,
    open: controlledOpen, onOpenChange, triggerClassName, badge,
}) {
    const panelId = `${useId().replace(/:/g, '')}-pop`;
    const [ownOpen, setOwnOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : ownOpen;
    const wrapRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const setOpen = useCallback((next) => {
        if (!isControlled) setOwnOpen(next);
        onOpenChange?.(next);
    }, [isControlled, onOpenChange]);

    const close = useCallback(() => setOpen(false), [setOpen]);
    useDismiss(wrapRef, close, open);

    useEffect(() => {
        if (!open) return;
        const first = focusableIn(panelRef.current)[0] || panelRef.current;
        first?.focus();
    }, [open]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        const fit = () => fitPanelToViewport(panelRef.current, align);
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, [open, align, width]);

    const onPanelKeyDown = (e) => {
        if (e.key !== 'Escape') return;
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
    };

    return (
        <div className="v2-pop-wrap" ref={wrapRef}>
            <button
                ref={triggerRef}
                type="button"
                className={triggerClassName || 'v2-btn v2-btn--default'}
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen(!open)}
            >
                {triggerContent || triggerLabel}
                {badge ? <span className="v2-badge">{badge}</span> : null}
            </button>
            {open && (
                <div
                    ref={panelRef}
                    id={panelId}
                    className={`v2-pop v2-pop--${align}`}
                    role="dialog"
                    aria-label={title || triggerLabel}
                    tabIndex={-1}
                    style={width ? { width } : undefined}
                    onKeyDown={onPanelKeyDown}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

Popover.propTypes = {
    triggerLabel: PropTypes.string.isRequired,
    triggerContent: PropTypes.node,
    title: PropTypes.string,
    children: PropTypes.node,
    align: PropTypes.oneOf(['left', 'right']),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    open: PropTypes.bool,
    onOpenChange: PropTypes.func,
    triggerClassName: PropTypes.string,
    badge: PropTypes.node,
};
